import { logDebug, reportError } from './services/errorTracking';

const TARGET_SAMPLE_RATE = 16000;


function convertFloat32ToInt16(float32Array) {
  const output = new Int16Array(float32Array.length);

  for (let i = 0; i < float32Array.length; i += 1) {
    const sample = Math.max(
      -1,
      Math.min(1, float32Array[i])
    );

    output[i] =
      sample < 0
        ? sample * 0x8000
        : sample * 0x7fff;
  }

  return output;
}


/*
 * The browser's audio context runs at the sample rate of the default OUTPUT
 * device. A Bluetooth headset in hands-free mode makes that 8 or 16 kHz -
 * below the 16 kHz the speech recogniser is told it receives. Sending those
 * samples unchanged plays speech back too fast and pitched up, which is
 * recognised as noise, so the missing samples are interpolated instead.
 */
function upsampleBuffer(buffer, inputSampleRate) {
  const ratio = TARGET_SAMPLE_RATE / inputSampleRate;
  const newLength = Math.round(buffer.length * ratio);
  const result = new Float32Array(newLength);

  for (let i = 0; i < newLength; i += 1) {
    const position = i / ratio;
    const before = Math.min(Math.floor(position), buffer.length - 1);
    const after = Math.min(before + 1, buffer.length - 1);
    const fraction = position - before;

    result[i] = buffer[before] + (buffer[after] - buffer[before]) * fraction;
  }

  return result;
}


function downsampleBuffer(
  buffer,
  inputSampleRate
) {
  if (inputSampleRate === TARGET_SAMPLE_RATE) {
    return convertFloat32ToInt16(buffer);
  }

  if (inputSampleRate < TARGET_SAMPLE_RATE) {
    return convertFloat32ToInt16(
      upsampleBuffer(buffer, inputSampleRate)
    );
  }

  const ratio =
    inputSampleRate / TARGET_SAMPLE_RATE;

  const newLength =
    Math.round(buffer.length / ratio);

  const result =
    new Float32Array(newLength);

  let resultOffset = 0;
  let bufferOffset = 0;

  while (resultOffset < result.length) {
    const nextBufferOffset =
      Math.round(
        (resultOffset + 1) * ratio
      );

    let accumulator = 0;
    let count = 0;

    for (
      let i = bufferOffset;
      i < nextBufferOffset &&
      i < buffer.length;
      i += 1
    ) {
      accumulator += buffer[i];
      count += 1;
    }

    result[resultOffset] =
      count > 0
        ? accumulator / count
        : 0;

    resultOffset += 1;
    bufferOffset = nextBufferOffset;
  }

  return convertFloat32ToInt16(result);
}


function connectWebSocket(wsUrl, onMessage, onDisconnected) {
  return new Promise((resolve, reject) => {
    const ws =
      new WebSocket(
        wsUrl
      );

    ws.binaryType = "arraybuffer";

    let opened = false;


    ws.onmessage = (event) => {
      try {
        const data =
          JSON.parse(event.data);

        onMessage(data);
      } catch (err) {
        reportError('websocket_message_parse', err);
      }
    };


    ws.onopen = () => {
      opened = true;

      logDebug('audio', 'WebSocket connected');

      resolve(ws);
    };


    ws.onerror = (event) => {
      reportError('websocket', event);

      if (!opened) {
        reject(
          new Error(
            "Unable to connect to KrishvaQ backend."
          )
        );
      }
    };


    ws.onclose = (event) => {
      logDebug('audio', `WebSocket closed (code ${event.code})`);

      // Only a post-connection drop that WASN'T requested by the caller
      // (see __explicitClose, set by startPcmStream's cleanup()) counts
      // as an "unexpected disconnect" worth reacting to (e.g. attempt
      // reconnect) — a close during the initial handshake is already
      // surfaced via onerror's rejection above.
      if (opened && onDisconnected && !ws.__explicitClose) {
        onDisconnected(event);
      }
    };
  });
}


async function startPcmStream(
  mediaStream,
  wsUrl,
  onMessage,
  onStopped,
  onDisconnected,
  // Optional: an AudioContext the caller already created and resumed
  // inside the user's tap (see startMicStream). Created here otherwise.
  existingAudioContext = null
) {
  const audioTracks =
    mediaStream.getAudioTracks();

  if (audioTracks.length === 0) {
    mediaStream
      .getTracks()
      .forEach((track) => track.stop());

    throw new Error(
      "No audio track was shared. " +
      "Select the Google Meet tab and enable tab audio."
    );
  }


  logDebug('audio', `Shared audio track: ${audioTracks[0].label}`);


  const ws =
    await connectWebSocket(
      wsUrl,
      onMessage,
      onDisconnected
    );


  const audioContext =
    existingAudioContext || createAudioContext();


  if (
    audioContext.state === "suspended"
  ) {
    await audioContext.resume();
  }


  logDebug('audio', `Browser sample rate: ${audioContext.sampleRate}`);


  const sourceNode =
    audioContext.createMediaStreamSource(
      mediaStream
    );


  const processorNode =
    audioContext.createScriptProcessor(
      4096,
      1,
      1
    );


  /*
   * ScriptProcessor must remain connected,
   * but we do NOT want to replay meeting
   * audio through KrishvaQ and create echo.
   */
  const silentGain =
    audioContext.createGain();

  silentGain.gain.value = 0;


  processorNode.onaudioprocess =
    (event) => {
      if (
        ws.readyState !== WebSocket.OPEN
      ) {
        return;
      }

      const input =
        event.inputBuffer
          .getChannelData(0);

      const pcm16 =
        downsampleBuffer(
          input,
          audioContext.sampleRate
        );

      ws.send(pcm16.buffer);
    };


  sourceNode.connect(
    processorNode
  );

  processorNode.connect(
    silentGain
  );

  silentGain.connect(
    audioContext.destination
  );


  logDebug('audio', 'Audio streaming started');


  let stopped = false;


  const cleanup = () => {
    if (stopped) {
      return;
    }

    stopped = true;

    logDebug('audio', 'Stopping audio stream');


    processorNode.onaudioprocess =
      null;


    try {
      processorNode.disconnect();
    } catch (_) {}


    try {
      sourceNode.disconnect();
    } catch (_) {}


    try {
      silentGain.disconnect();
    } catch (_) {}


    mediaStream
      .getTracks()
      .forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });


    if (
      ws.readyState === WebSocket.OPEN ||
      ws.readyState === WebSocket.CONNECTING
    ) {
      // Marks this as a deliberate close so connectWebSocket's onclose
      // handler doesn't report it to the caller as an unexpected
      // disconnect (which would otherwise trigger a pointless reconnect
      // attempt right after the user paused/ended the meeting).
      ws.__explicitClose = true;
      ws.close();
    }


    audioContext
      .close()
      .catch(() => {});


    if (onStopped) {
      onStopped();
    }
  };


  /*
   * Chrome fires "ended" when the user
   * clicks "Stop sharing".
   */
  audioTracks.forEach((track) => {
    track.addEventListener(
      "ended",
      cleanup,
      { once: true }
    );
  });


  /*
   * The control channel rides the SAME socket as the audio (the backend
   * accepts a TEXT frame alongside the binary ones — see
   * MeetingSession.handle_control_message). Attached to the cleanup
   * handle rather than changing this function's return type, so every
   * existing caller that treats the result purely as "the teardown
   * function" keeps working untouched.
   */
  cleanup.sendControl = (payload) => {
    if (ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      reportError('websocket_control_send', err);
      return false;
    }
  };


  return cleanup;
}


/*
 * options.hearOthers: the microphone is the ONLY way this session can hear
 * the other participants - a phone or tablet, which cannot capture a
 * meeting tab - so it has to pick up the meeting playing on the speaker.
 * Echo cancellation and noise suppression exist to REMOVE exactly that
 * (speaker audio, steady background voices) from a microphone, and on
 * Android they are applied against the whole device's playback, not just
 * this page's: with them on, the other participants were filtered out
 * before a single sample reached the transcript. They are therefore off
 * in this mode; gain control stays on so a distant speaker is still heard.
 * Nothing in the page plays the meeting back, so there is no feedback loop
 * for the cancellation to prevent.
 */
export async function startMicStream(
  wsUrl,
  onMessage,
  onStopped,
  deviceId,
  onDisconnected,
  options = {}
) {
  logDebug('audio', 'Requesting microphone...');

  const hearOthers = Boolean(options && options.hearOthers);

  // iOS only lets an AudioContext start inside the tap that asked for it,
  // and rejects a later resume() with NotAllowedError. So it is created
  // and resumed here, before the first await; by the time startPcmStream
  // uses it the microphone and WebSocket round trips have ended the tap.
  const audioContext = createAudioContext();
  audioContext.resume().catch(() => {});

  let mediaStream = null;

  try {
    mediaStream =
      await navigator.mediaDevices
        .getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: !hearOthers,
            noiseSuppression: !hearOthers,
            autoGainControl: true,
            ...(deviceId
              ? { deviceId: { exact: deviceId } }
              : {}),
          },
        });


    logDebug('audio', 'Microphone access granted');


    return await startPcmStream(
      mediaStream,
      wsUrl,
      onMessage,
      onStopped,
      onDisconnected,
      audioContext
    );
  } catch (err) {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
    }
    audioContext.close().catch(() => {});
    throw err;
  }
}

function createAudioContext() {
  const AudioContextClass =
    window.AudioContext ||
    window.webkitAudioContext;

  return new AudioContextClass();
}


/*
 * Wire protocol v2 (see backend routers/websocket.py, mirrored by the
 * desktop app): one tag byte in front of every binary frame says which
 * capture source produced it, so ONE socket can carry the shared tab's
 * audio (system_audio) and the local microphone (microphone) as two
 * independent STT streams on one meeting timeline.
 */
const AUDIO_PROTOCOL_V2 = 2;
const WIRE_TAG_MICROPHONE = 1;
const WIRE_TAG_SYSTEM_AUDIO = 2;
const PROTOCOL_OFFER_TIMEOUT_MS = 3000;

function tagFrame(tag, pcm16) {
  const out = new Uint8Array(1 + pcm16.byteLength);
  out[0] = tag;
  out.set(
    new Uint8Array(pcm16.buffer, pcm16.byteOffset, pcm16.byteLength),
    1
  );
  return out.buffer;
}

/*
 * "default" and "communications" are not devices: they are aliases the
 * browser re-points at whatever the operating system currently treats as the
 * default input. Capturing one means the microphone can change underneath a
 * running meeting - for instance when a Bluetooth headset connects - and the
 * transcript's "my audio" then silently becomes a different person's voice.
 */
const MICROPHONE_ALIASES = ['', 'default', 'communications'];

function isConcreteDevice(deviceId) {
  return !!deviceId && !MICROPHONE_ALIASES.includes(deviceId);
}

/*
 * The physical device a default-input alias points at, found among `devices`,
 * or null when that cannot be told with certainty. The alias is labelled
 * "<prefix> - <the device's own label>" (the prefix is localised) and shares
 * the device's group. Only a device matching on BOTH - and only when exactly
 * one does - is taken, so a different input of the same sound card (a loopback
 * such as "Stereo Mix", say) can never be picked by mistake.
 */
function physicalDefaultMicrophone(devices, requested) {
  const inputs = devices.filter((device) => device.kind === 'audioinput');
  const alias = inputs.find(
    (device) => device.deviceId === (requested || 'default')
  );

  if (!alias || !alias.groupId) {
    return null;
  }

  const aliasLabel = alias.label || '';
  const physical = inputs.filter(
    (device) =>
      isConcreteDevice(device.deviceId) &&
      device.groupId === alias.groupId &&
      !!device.label &&
      (aliasLabel === device.label || aliasLabel.endsWith(` - ${device.label}`))
  );

  return physical.length === 1 ? physical[0] : null;
}

/*
 * Turns the default-input alias into the physical device it points at right
 * now, so the meeting captures THAT device for its whole length. A device the
 * caller already named is returned unchanged. When the browser does not reveal
 * which device the alias is (no microphone permission yet, no group ids), the
 * request is returned as it was and capture behaves exactly as before.
 */
async function resolveMicrophoneDeviceId(requestedId) {
  const requested = requestedId || '';

  if (isConcreteDevice(requested)) {
    return requested;
  }

  try {
    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.enumerateDevices !== 'function'
    ) {
      return requested;
    }

    const physical = physicalDefaultMicrophone(
      await navigator.mediaDevices.enumerateDevices(),
      requested
    );

    return physical ? physical.deviceId : requested;
  } catch (_) {
    return requested;
  }
}

/*
 * The device a microphone was on, found again among `devices`: by its id or -
 * when the browser gave a returning Bluetooth device a new id - by its exact
 * name, but only if exactly one device has that name. Never anything else.
 */
function findSameMicrophone(devices, identity) {
  const inputs = devices.filter(
    (device) => device.kind === 'audioinput' && isConcreteDevice(device.deviceId)
  );

  const byId = inputs.find((device) => device.deviceId === identity.deviceId);

  if (byId) {
    return byId;
  }

  if (!identity.label) {
    return null;
  }

  const sameName = inputs.filter((device) => device.label === identity.label);

  return sameName.length === 1 ? sameName[0] : null;
}

/*
 * The microphone the user chose: the id they picked and the name it had. A
 * Bluetooth headset can return under a NEW id after a profile switch, and the
 * id that was chosen is then stale; the same device is found again by its
 * exact name. Only a concrete device is ever remembered - choosing the
 * system default is not choosing a device.
 */
const chosenMicrophone = { requestedId: '', label: '' };

export function rememberMicrophoneChoice(deviceId, label) {
  chosenMicrophone.requestedId = isConcreteDevice(deviceId) ? deviceId : '';
  chosenMicrophone.label = chosenMicrophone.requestedId ? label || '' : '';
}

/*
 * Console diagnostics for the microphone pipeline. Deliberately NOT logDebug:
 * that is silent in production builds, which is exactly where a Bluetooth
 * problem has to be diagnosed. Low volume (events and a few early summaries,
 * never a line per frame) and only device names, ids and state - never audio
 * or speech.
 */
function micDiagnostic(message) {
  // eslint-disable-next-line no-console
  console.info(`[mic-diagnostics] ${message}`);
}

function firstAudioTrack(stream) {
  return stream && typeof stream.getAudioTracks === 'function'
    ? stream.getAudioTracks()[0]
    : undefined;
}

function trackState(stream) {
  const track = firstAudioTrack(stream);

  return track
    ? `readyState=${track.readyState} enabled=${track.enabled} muted=${track.muted}`
    : 'no track';
}

function settingsSummary(stream) {
  const track = firstAudioTrack(stream);
  const settings =
    track && typeof track.getSettings === 'function' ? track.getSettings() : {};

  return (
    `groupId=${shortId(settings.groupId)} sampleRate=${settings.sampleRate} channels=${settings.channelCount} ` +
    `echoCancellation=${settings.echoCancellation} ` +
    `noiseSuppression=${settings.noiseSuppression} autoGainControl=${settings.autoGainControl}`
  );
}

function shortId(deviceId) {
  return deviceId ? String(deviceId).slice(0, 8) : 'none';
}

/*
 * Once per capture: the inputs and outputs the browser can see, and where the
 * audio context plays. Names and short ids only. It shows whether the
 * microphone and the speakers are the same physical device - the usual way
 * the meeting's own sound reaches a microphone.
 */
async function logAudioRouting(audioContext) {
  try {
    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.enumerateDevices !== 'function'
    ) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const describe = (kind) =>
      devices
        .filter((device) => device.kind === kind)
        .map(
          (device) =>
            `"${device.label || 'unnamed'}" id=${shortId(device.deviceId)} group=${shortId(device.groupId)}`
        )
        .join('; ') || 'none';
    const sink = audioContext.sinkId;

    micDiagnostic(`audio inputs: ${describe('audioinput')}`);
    micDiagnostic(
      `audio outputs: ${describe('audiooutput')}; the audio context plays to ${
        typeof sink === 'string' ? sink || 'the system default' : 'a custom sink'
      }`
    );
  } catch (_) {
    /* diagnostics only */
  }
}

// A microphone that has delivered (almost) exactly zero for this long while
// open is not going to produce a transcript; below SILENT_PEAK counts towards
// that, at or above AUDIBLE_PEAK ends it.
const SILENT_PEAK = 1e-4;
const AUDIBLE_PEAK = 1e-3;
const SILENT_AFTER_MS = 10000;
// At most this many automatic re-opens of the microphone in this window.
const MAX_RESTARTS_PER_WINDOW = 3;
const RESTART_WINDOW_MS = 60000;
// One-line summaries of the capture, this long after it started.
const HEALTH_LOG_DELAYS_MS = [5000, 15000, 30000, 60000, 180000];
// Peak levels (0..1) that count as the meeting tab playing sound and as the
// microphone carrying speech-level sound, for the echo check in the summaries.
const TAB_LOUD_PEAK = 0.02;
const MIC_SPEECH_PEAK = 0.01;

function microphoneConstraints(deviceId) {
  return {
    channelCount: 1,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  };
}

// Why the microphone could not be opened, for the caller's notice. A device
// that is gone is told apart from a refusal; anything else keeps the reason
// it always had.
function microphoneFailureReason(err) {
  const name = err && err.name;

  if (name === 'OverconstrainedError' || name === 'NotFoundError') {
    return 'not_found';
  }

  return 'denied';
}

function describeMicrophone(stream) {
  const track = stream.getAudioTracks()[0];

  if (!track) {
    return { deviceId: '', label: '' };
  }

  const settings =
    typeof track.getSettings === 'function' ? track.getSettings() : {};

  return {
    deviceId: settings.deviceId || '',
    label: track.label || '',
  };
}

/*
 * The chosen device when it is gone under its old id but still plugged in
 * under a new one (a Bluetooth headset after a profile switch): its new id,
 * found by the exact name it had. Empty in every other case - a device that
 * is really gone stays gone and is reported, never swapped for another.
 */
async function sameMicrophoneUnderNewId(deviceId, err) {
  if (
    !isConcreteDevice(deviceId) ||
    microphoneFailureReason(err) !== 'not_found' ||
    chosenMicrophone.requestedId !== deviceId ||
    !chosenMicrophone.label ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.enumerateDevices !== 'function'
  ) {
    return '';
  }

  try {
    const same = findSameMicrophone(
      await navigator.mediaDevices.enumerateDevices(),
      { deviceId, label: chosenMicrophone.label }
    );

    return same && same.deviceId !== deviceId ? same.deviceId : '';
  } catch (_) {
    return '';
  }
}

/*
 * Opens the microphone that was asked for - that device, or for a default-input
 * alias the physical device it points at right now - and nothing else.
 */
async function openMicrophone(requestedId) {
  const deviceId = await resolveMicrophoneDeviceId(requestedId);

  micDiagnostic(
    `opening the microphone: requested ${
      requestedId ? shortId(requestedId) : 'the system default'
    }, using ${deviceId ? shortId(deviceId) : 'the system default'}`
  );

  let stream;

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: microphoneConstraints(deviceId),
    });
  } catch (err) {
    const renamed = await sameMicrophoneUnderNewId(deviceId, err);

    if (!renamed) {
      throw err;
    }

    micDiagnostic(
      `the chosen microphone has a new id (${shortId(deviceId)} -> ${shortId(renamed)}); using it`
    );
    stream = await navigator.mediaDevices.getUserMedia({
      audio: microphoneConstraints(renamed),
    });
  }

  if (isConcreteDevice(requestedId)) {
    try {
      chosenMicrophone.requestedId = requestedId;
      chosenMicrophone.label =
        describeMicrophone(stream).label || chosenMicrophone.label;
    } catch (_) {
      /* the name is only a convenience for finding the device again */
    }
  }

  return stream;
}

/*
 * What the capture layer knows about the microphone, for the page to show:
 * detail.state is 'started' (with the device's name, and followsSystem when it
 * was asked for as the system default), 'stopped', 'muted',
 * 'unmuted', 'silent' (open but no sound for a while), 'audible' (sound again)
 * or 'default_changed' (the system default is now another device, with its
 * name). An EventTarget rather than more callback arguments, so the
 * existing call shapes between the page, useAudio and this file stay as they
 * are.
 */
export const microphoneEvents = new EventTarget();

function announceMicrophone(detail) {
  try {
    microphoneEvents.dispatchEvent(new CustomEvent('change', { detail }));
  } catch (_) {
    /* a status announcement must never break capture */
  }
}

/*
 * Tab audio + microphone over one v2 socket. Returns the same kind of
 * cleanup handle as startPcmStream, or null when the backend does not
 * offer v2 (the caller then falls back to tab-only v1 so nothing is lost).
 * Each source is its own tap: the microphone ending (unplugged) stops
 * only the microphone; the tab share ending stops everything, exactly as
 * before.
 */
async function startDualPcmStream(
  tabStream,
  micStream,
  wsUrl,
  onMessage,
  onStopped,
  onDisconnected,
  onMicrophoneUnavailable,
  followedAlias = ''
) {
  let resolveOffer;
  const offered = new Promise((resolve) => {
    resolveOffer = resolve;
  });

  const ws = await connectWebSocket(
    wsUrl,
    (data) => {
      if (
        data &&
        data.type === 'audio_protocol_negotiated' &&
        data.version === AUDIO_PROTOCOL_V2
      ) {
        resolveOffer(true);
        return;
      }
      onMessage(data);
    },
    onDisconnected
  );

  const gotOffer = await Promise.race([
    offered,
    new Promise((resolve) =>
      setTimeout(() => resolve(false), PROTOCOL_OFFER_TIMEOUT_MS)
    ),
  ]);

  if (!gotOffer) {
    logDebug('audio', 'Backend did not offer audio protocol v2');
    ws.__explicitClose = true;
    ws.close();
    return null;
  }

  // Must precede the first binary frame - the backend only decodes tagged
  // frames once it has seen this.
  ws.send(
    JSON.stringify({
      type: 'audio_protocol_confirmed',
      version: AUDIO_PROTOCOL_V2,
    })
  );

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContextClass();
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  const taps = [];

  const addTap = (mediaStream, tag, name, onFrame) => {
    const sourceNode = audioContext.createMediaStreamSource(mediaStream);
    const processorNode = audioContext.createScriptProcessor(4096, 1, 1);
    // Never replay captured audio through KrishvaQ (echo).
    const silentGain = audioContext.createGain();
    silentGain.gain.value = 0;

    processorNode.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);

      if (onFrame) {
        onFrame(input);
      }

      if (ws.readyState !== WebSocket.OPEN) {
        return;
      }
      const pcm16 = downsampleBuffer(input, audioContext.sampleRate);
      ws.send(tagFrame(tag, pcm16));
    };

    sourceNode.connect(processorNode);
    processorNode.connect(silentGain);
    silentGain.connect(audioContext.destination);

    const tap = {
      name,
      stop: () => {
        processorNode.onaudioprocess = null;
        [processorNode, sourceNode, silentGain].forEach((node) => {
          try {
            node.disconnect();
          } catch (_) {}
        });
        mediaStream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (_) {}
        });
      },
    };
    taps.push(tap);
    logDebug('audio', `LIVE_MEETING_${name}_STARTED`);
    return tap;
  };

  // What the capture has seen so far - numbers only, never audio - for the
  // diagnostics below and for noticing a microphone that is connected but
  // delivers no sound at all.
  let tabFrames = 0;
  const mic = { frames: 0, peak: 0, silentMs: 0, silent: false, everAudible: false };

  // Echo check: how often the microphone carries speech-level sound while the
  // meeting tab is playing, against how often it does while the tab is quiet.
  // A microphone that mostly "speaks" only while the tab is loud is hearing the
  // tab (speakers into the microphone) rather than the user. Numbers only; the
  // two taps run on the same audio clock, so "while" means within one buffer.
  const leak = {
    tabLoudFrames: 0,
    micFramesWhileTabLoud: 0,
    micLoudWhileTabLoud: 0,
    micLoudWhileTabQuiet: 0,
  };
  let lastLoudTabFrame = -10;

  const onTabFrame = (input) => {
    tabFrames += 1;

    let peak = 0;

    for (let i = 0; i < input.length; i += 1) {
      const level = Math.abs(input[i]);
      if (level > peak) {
        peak = level;
      }
    }

    if (peak >= TAB_LOUD_PEAK) {
      leak.tabLoudFrames += 1;
      lastLoudTabFrame = tabFrames;
    }
  };

  addTap(tabStream, WIRE_TAG_SYSTEM_AUDIO, 'TAB_AUDIO', onTabFrame);

  let stopped = false;
  // The tap carrying the microphone, or null while there is none.
  let micTap = null;
  let currentMicStream = null;
  let restartingMic = false;
  // Which device the microphone is on, so the SAME device (never another
  // one) can be brought back; empty when it was only an alias.
  const micIdentity = { deviceId: '', label: '' };
  // Set once a default-input change has been announced, per device.
  let announcedDefault = '';
  // A silent microphone is re-opened once per silent spell, no more.
  let silentRecoveryUsed = false;
  // Automatic re-opens are event-driven and also capped, so a device that
  // keeps flapping cannot cause a loop.
  const restartTimes = [];

  const restartAllowed = () => {
    const now = Date.now();

    while (restartTimes.length && now - restartTimes[0] > RESTART_WINDOW_MS) {
      restartTimes.shift();
    }

    if (restartTimes.length >= MAX_RESTARTS_PER_WINDOW) {
      return false;
    }

    restartTimes.push(now);
    return true;
  };

  // Brings the microphone back on the device it was on. Never a different
  // device, and only when the microphone is down and nothing is already
  // restarting it. `planned` is a deliberate re-open of a microphone that was
  // still running (silent), so a failure there must be reported.
  const restartMicrophone = async (deviceId, planned = false) => {
    if (stopped || restartingMic || micTap) {
      return false;
    }

    if (!restartAllowed()) {
      micDiagnostic('not re-opening the microphone again: too many recent attempts');
      return false;
    }

    restartingMic = true;
    micDiagnostic(`re-opening the microphone (${planned ? 'it was silent' : 'it returned'})`);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: microphoneConstraints(deviceId),
      });

      if (stopped || micTap) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      // eslint-disable-next-line no-use-before-define
      attachMicrophone(stream);
      return true;
    } catch (err) {
      reportError('meeting_tab_microphone_restart', err);
      micDiagnostic(`re-opening the microphone failed: ${(err && err.name) || 'error'}`);

      if (planned) {
        announceMicrophone({ state: 'stopped', reason: 'restart_failed' });
        if (onMicrophoneUnavailable) {
          onMicrophoneUnavailable(microphoneFailureReason(err));
        }
      }
      return false;
    } finally {
      restartingMic = false;
    }
  };

  // Called when the device list changes AND right after the microphone's
  // track ends (a Bluetooth profile switch can end it after the last list
  // change, and nothing else would ever try again). Event-driven only - never
  // a timer - so a microphone that stays gone cannot cause a retry loop.
  const handleDeviceChange = async () => {
    if (stopped || restartingMic) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      if (!micTap) {
        if (!micIdentity.deviceId) {
          return;
        }

        const same = findSameMicrophone(devices, micIdentity);

        if (same) {
          await restartMicrophone(same.deviceId);
        }
        return;
      }

      // The microphone is running. If it was chosen by following the system
      // default and the default is now ANOTHER device, say so - never switch
      // silently, never ignore it silently either.
      if (followedAlias && micIdentity.deviceId) {
        const current = physicalDefaultMicrophone(devices, followedAlias);

        if (
          current &&
          current.deviceId !== micIdentity.deviceId &&
          current.deviceId !== announcedDefault
        ) {
          announcedDefault = current.deviceId;
          micDiagnostic(`the system default microphone is now "${current.label}"`);
          announceMicrophone({ state: 'default_changed', label: current.label });
        }
      }
    } catch (_) {
      /* best effort - the notice already says what state the microphone is in */
    }
  };

  // A microphone that was opened and has NEVER delivered any sound (a headset
  // that has just switched profile, or one held by another device) gives
  // nothing to transcribe. Re-open the same device once; if it is still dead
  // the user is told. A microphone that has produced sound and then goes quiet
  // is just a quiet room and is left alone.
  const recoverSilentMicrophone = async () => {
    if (
      silentRecoveryUsed ||
      stopped ||
      restartingMic ||
      !micTap ||
      !micIdentity.deviceId
    ) {
      return;
    }

    silentRecoveryUsed = true;

    const silentTap = micTap;
    micTap = null;
    silentTap.stop();

    await restartMicrophone(micIdentity.deviceId, true);
  };

  const onMicFrame = (input) => {
    let peak = 0;

    for (let i = 0; i < input.length; i += 1) {
      const level = Math.abs(input[i]);
      if (level > peak) {
        peak = level;
      }
    }

    mic.frames += 1;
    if (peak > mic.peak) {
      mic.peak = peak;
    }

    if (tabFrames - lastLoudTabFrame <= 1) {
      leak.micFramesWhileTabLoud += 1;

      if (peak >= MIC_SPEECH_PEAK) {
        leak.micLoudWhileTabLoud += 1;
      }
    } else if (peak >= MIC_SPEECH_PEAK) {
      leak.micLoudWhileTabQuiet += 1;
    }

    const frameMs = (input.length / audioContext.sampleRate) * 1000;

    if (peak >= SILENT_PEAK) {
      mic.silentMs = 0;
    }

    if (peak >= AUDIBLE_PEAK) {
      mic.everAudible = true;

      if (mic.silent) {
        mic.silent = false;
        silentRecoveryUsed = false;
        micDiagnostic('the microphone is delivering sound again');
        announceMicrophone({ state: 'audible' });
      }
    } else if (peak < SILENT_PEAK && !mic.everAudible) {
      mic.silentMs += frameMs;

      if (!mic.silent && mic.silentMs >= SILENT_AFTER_MS) {
        mic.silent = true;
        micDiagnostic(
          `the microphone has delivered no sound at all since it was opened, ${Math.round(mic.silentMs / 1000)}s ago (${trackState(currentMicStream)})`
        );
        announceMicrophone({ state: 'silent' });
        recoverSilentMicrophone();
      }
    }
  };

  const attachMicrophone = (stream) => {
    const tap = addTap(stream, WIRE_TAG_MICROPHONE, 'MIC', onMicFrame);
    micTap = tap;
    currentMicStream = stream;
    mic.silentMs = 0;
    mic.silent = false;
    mic.everAudible = false;

    const info = describeMicrophone(stream);
    micIdentity.deviceId = isConcreteDevice(info.deviceId) ? info.deviceId : '';
    micIdentity.label = info.label;
    micDiagnostic(
      `microphone started: "${info.label || 'unnamed'}" device=${shortId(info.deviceId)} ` +
        `${trackState(stream)} ${settingsSummary(stream)} audioContext=${audioContext.state}@${audioContext.sampleRate}Hz`
    );
    announceMicrophone({
      state: 'started',
      ...info,
      followsSystem: !!followedAlias,
    });

    stream.getAudioTracks().forEach((track) => {
      // A microphone that goes away must not take the tab audio with it.
      track.addEventListener(
        'ended',
        () => {
          if (stopped || micTap !== tap) {
            return;
          }
          tap.stop();
          micTap = null;
          micDiagnostic(`the microphone track ended (${trackState(stream)})`);
          announceMicrophone({ state: 'stopped', reason: 'ended' });
          if (onMicrophoneUnavailable) {
            onMicrophoneUnavailable('ended');
          }
          // The device may already be back; waiting for another device-list
          // change that never comes would leave the microphone off for good.
          handleDeviceChange();
        },
        { once: true }
      );

      // A track that is muted rather than ended (the operating system muted
      // it, or a Bluetooth headset switched profile) delivers no speech but
      // never fires "ended" - say so instead of leaving a silent gap.
      track.addEventListener('mute', () => {
        if (!stopped && micTap === tap) {
          micDiagnostic(`the microphone track was muted (${trackState(stream)})`);
          announceMicrophone({ state: 'muted' });
        }
      });
      track.addEventListener('unmute', () => {
        if (!stopped && micTap === tap) {
          micDiagnostic(`the microphone track is live again (${trackState(stream)})`);
          announceMicrophone({ state: 'unmuted' });
        }
      });
    });
  };

  micDiagnostic(
    `audio context: state=${audioContext.state} sampleRate=${audioContext.sampleRate}Hz; ` +
      `the microphone was requested as ${
        followedAlias ? `the system "${followedAlias}" input` : 'a chosen device'
      }`
  );

  attachMicrophone(micStream);
  logAudioRouting(audioContext);

  const watchesDevices = !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.addEventListener === 'function'
  );

  if (watchesDevices) {
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
  }

  // A few one-line summaries early in the capture - enough to see where local
  // speech stops (no frames / silent / sent but not transcribed) without a log
  // line per frame.
  const healthTimers = HEALTH_LOG_DELAYS_MS.map((delay) =>
    setTimeout(() => {
      micDiagnostic(
        `health after ${delay / 1000}s: microphone ${micTap ? 'running' : 'stopped'}, ` +
          `frames=${mic.frames}, peak=${mic.peak.toFixed(4)}, everAudible=${mic.everAudible}, ` +
          `silentFor=${Math.round(mic.silentMs / 1000)}s, ` +
          `${trackState(currentMicStream)}; tab frames=${tabFrames}; ` +
          `echo check: tabLoud=${leak.tabLoudFrames} micFramesWhileTabLoud=${leak.micFramesWhileTabLoud} ` +
          `micLoudWhileTabLoud=${leak.micLoudWhileTabLoud} micLoudWhileTabQuiet=${leak.micLoudWhileTabQuiet}; ` +
          `audioContext=${audioContext.state}@${audioContext.sampleRate}Hz; socket=${ws.readyState}`
      );
      mic.peak = 0;
    }, delay)
  );

  const cleanup = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    logDebug('audio', 'Stopping audio stream');

    healthTimers.forEach((timer) => clearTimeout(timer));
    taps.forEach((tap) => tap.stop());

    if (watchesDevices) {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      );
    }

    announceMicrophone({ state: 'stopped', reason: 'session' });

    if (
      ws.readyState === WebSocket.OPEN ||
      ws.readyState === WebSocket.CONNECTING
    ) {
      ws.__explicitClose = true;
      ws.close();
    }

    audioContext.close().catch(() => {});

    if (onStopped) {
      onStopped();
    }
  };

  // "Stop sharing" in Chrome ends the meeting capture as a whole.
  tabStream.getAudioTracks().forEach((track) => {
    track.addEventListener('ended', cleanup, { once: true });
  });

  cleanup.sendControl = (payload) => {
    if (ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      reportError('websocket_control_send', err);
      return false;
    }
  };

  return cleanup;
}

/*
 * options (all optional - omitted means the original tab-only behaviour):
 *   includeMicrophone         also capture the local microphone
 *   microphoneDeviceId        which microphone; the default input (or empty)
 *                             is pinned to the physical device it points at
 *   onMicrophoneUnavailable   called with a reason when the microphone
 *                             cannot be used (denied/unplugged); the tab
 *                             audio keeps running regardless.
 */
export async function startMeetingStream(
  wsUrl,
  onMessage,
  onStopped,
  onDisconnected,
  options = {}
) {
  logDebug('audio', 'Requesting meeting/tab audio...');

  // Phone and tablet browsers have no tab capture: `getDisplayMedia` is
  // simply not there, and calling it would surface as the browser's own
  // "getDisplayMedia is not a function". Callers choose the microphone for
  // such a browser (see constants/audioSources.js's resolveCaptureSource);
  // this is the backstop, and says what is actually wrong.
  if (
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getDisplayMedia !== 'function'
  ) {
    throw new Error(
      "This browser can't share a meeting tab. Open the meeting on a " +
      'computer, or use a microphone meeting instead.'
    );
  }


  /*
   * IMPORTANT:
   *
   * When Chrome opens the picker:
   *
   *   1. Choose "Chrome Tab"
   *   2. Choose the tab with your meeting (Google Meet, Teams Web,
   *      Zoom Web, Webex Web, ...)
   *   3. Enable "Share tab audio"
   *   4. Click Share
   */
  const mediaStream =
    await navigator.mediaDevices
      .getDisplayMedia({
        video: {
          displaySurface: "browser",
        },

        audio: true,

        selfBrowserSurface: "exclude",

        /*
         * We want Meet/tab audio rather than
         * accidentally capturing every sound
         * from the computer.
         */
        systemAudio: "exclude",

        surfaceSwitching: "include",
      });


  if (
    mediaStream.getAudioTracks().length === 0
  ) {
    mediaStream
      .getTracks()
      .forEach((track) => track.stop());

    throw new Error(
      "No meeting audio was shared. " +
      "Select the browser tab containing your meeting and enable " +
      "'Share tab audio'."
    );
  }


  logDebug('audio', 'Meeting audio access granted');


  /*
   * A shared meeting tab carries what the tab PLAYS - the remote
   * participants. The user's own voice goes to the other participants,
   * not back into the tab, so it is only in the transcript if the
   * microphone is captured too.
   */
  if (options.includeMicrophone) {
    let micStream = null;
    try {
      // A device that was chosen is opened exactly as chosen: if it is gone
      // that is reported, never swapped for another. Only the default-input
      // alias is resolved - to the physical device it points at right now,
      // which is then shown by name and kept for the whole meeting.
      micStream = await openMicrophone(options.microphoneDeviceId);
    } catch (err) {
      reportError('meeting_tab_microphone', err);
      micDiagnostic(`could not open the microphone: ${(err && err.name) || 'error'}`);
      if (options.onMicrophoneUnavailable) {
        options.onMicrophoneUnavailable(microphoneFailureReason(err));
      }
    }

    if (micStream) {
      let dual = null;
      try {
        const separator = wsUrl.includes('?') ? '&' : '?';
        dual = await startDualPcmStream(
          mediaStream,
          micStream,
          `${wsUrl}${separator}audio_protocol_version=${AUDIO_PROTOCOL_V2}`,
          onMessage,
          onStopped,
          onDisconnected,
          options.onMicrophoneUnavailable,
          isConcreteDevice(options.microphoneDeviceId)
            ? ''
            : options.microphoneDeviceId || 'default'
        );
      } catch (err) {
        micStream.getTracks().forEach((track) => track.stop());
        mediaStream.getTracks().forEach((track) => track.stop());
        throw err;
      }

      if (dual) {
        return dual;
      }

      // Backend without v2: tab-only, as before - say so.
      micStream.getTracks().forEach((track) => track.stop());
      if (options.onMicrophoneUnavailable) {
        options.onMicrophoneUnavailable('unsupported');
      }
    }
  }


  return startPcmStream(
    mediaStream,
    wsUrl,
    onMessage,
    onStopped,
    onDisconnected
  );
}
